/** The three positions the eligibility `ToggleButtonGroup` can show. */
export type EligibilitySelection = 'auto' | 'eligible' | 'ineligible';

export interface EligibilitySectionStateInput {
  /**
   * Whether any points/co-pay form value currently differs from the live value — the same diff
   * `buildSocieteRewardAction`/`diffValueFields` use to decide whether this action overrides a
   * value field (points or co-pay). Eligibility itself is NOT part of this — it can be overridden
   * on its own.
   */
  valuesEdited: boolean;
  /** Whether eligibility itself is already admin-owned, per `override_meta.fields`. */
  eligibilityOwned: boolean;
  /** The eligibility the form was seeded with when the dialog last (re)opened (from `buildInitialOverrideForm`). */
  seededEligible: boolean | null;
  /** The toggle's current selection. */
  currentSelection: EligibilitySelection;
  /**
   * Whether the admin has explicitly clicked the eligibility toggle since the dialog last
   * (re)opened — as opposed to its value only ever having been auto-preselected by this function.
   */
  userTouched: boolean;
}

export interface EligibilitySectionState {
  /** Whether the "Auto" segment should be disabled right now. */
  autoDisabled: boolean;
  /** Which tooltip explains the disabled "Auto" segment — `null` when it isn't disabled. */
  autoTooltip: 'owned' | 'values' | null;
  /**
   * The value the caller should force `form.eligible` to, or `undefined` to leave it exactly as
   * it is. `null` is a real, meaningful forced value here (force back to "Auto") — distinct from
   * `undefined` ("no change needed") — so a bare `boolean | null` can't carry both meanings; this
   * deliberately widens to `boolean | null | undefined`.
   */
  forcedSelection: boolean | null | undefined;
}

/**
 * Pure decision for the override dialog's dynamic eligibility section: whether the "Auto" segment
 * should be disabled right now (and why), and whether the toggle's current selection should be
 * force-changed.
 *
 * - **Eligibility owned**: Auto is always disabled with the "owned" tooltip, regardless of
 *   anything else (including `valuesEdited`) — there is no eligibility-only un-override action by
 *   design; CLEAR OVERRIDE is the only way back to Auto. Nothing is force-selected here: the
 *   dialog seeds the owned value once on open, and this function never re-forces it afterward.
 * - **Not owned, values edited**: Auto is disabled with the "values" tooltip — an overridden
 *   package can't stay auto-calculated. If the toggle is still sitting on Auto AND the admin
 *   hasn't explicitly touched it this session, force-select Eligible (the common case). An admin
 *   who already chose Ineligible (or Eligible) themselves keeps that choice untouched.
 * - **Not owned, values reverted** (back to nothing edited): Auto is valid again, so it's
 *   re-enabled. If the current selection was never a real admin choice (only ever the automatic
 *   preselect above), restore it to whatever the dialog originally seeded — which may itself be
 *   Auto (`null`) for a fresh package, or a real value for a legacy-overridden one (Rule 2b).
 *   Leaves the selection alone once the admin has made an explicit choice.
 */
export function eligibilitySectionState({
  valuesEdited,
  eligibilityOwned,
  seededEligible,
  currentSelection,
  userTouched,
}: EligibilitySectionStateInput): EligibilitySectionState {
  if (eligibilityOwned) {
    return { autoDisabled: true, autoTooltip: 'owned', forcedSelection: undefined };
  }

  if (valuesEdited) {
    const forcedSelection = !userTouched && currentSelection === 'auto' ? true : undefined;
    return { autoDisabled: true, autoTooltip: 'values', forcedSelection };
  }

  const forcedSelection = userTouched ? undefined : seededEligible;
  return { autoDisabled: false, autoTooltip: null, forcedSelection };
}
